export interface LearnPageMeta {
  slug: string
  path: string
  eyebrow: string
  /** Card / cross-link name, e.g. "Endometriosis" */
  shortTitle: string
  /** Page H1 */
  title: string
  /** "The one-sentence version" — used as the page lede and the teaser card body */
  oneLiner: string
  metaTitle: string
  metaDescription: string
  /** Only true for copy written or signed off by Tiger. Gates the reviewed-by line. */
  physicianReviewed: boolean
  /** Slug of the page to steer the reader toward next (the guided path). */
  next?: string
  /** One short line on why `next` is the natural next step. */
  nextNote?: string
}

export const LEARN_PAGES: LearnPageMeta[] = [
  {
    slug: 'endometriosis',
    path: '/endometriosis',
    eyebrow: 'Understanding your condition',
    shortTitle: 'Endometriosis',
    title: 'What is endometriosis?',
    oneLiner:
      'A disease where tissue similar to the lining of your uterus grows outside your uterus. But the real story isn’t the tissue. It’s why your body can’t stop it.',
    metaTitle: 'What Is Endometriosis? The Science, in Plain Language | Pelvia',
    metaDescription:
      'Endometriosis is not "bad periods." Understand the immune failure, the self-sustaining estrogen loop, progesterone resistance, and why it hurts, all in plain language.',
    physicianReviewed: true,
    next: 'excision-surgery',
    nextNote: 'The gold-standard treatment that removes the disease at the root.',
  },
  {
    slug: 'adenomyosis',
    path: '/adenomyosis',
    eyebrow: 'Understanding your condition',
    shortTitle: 'Adenomyosis',
    title: 'What is adenomyosis?',
    oneLiner:
      'When the lining of your uterus invades the muscle wall of the uterus itself, and the muscle wall fights back in all the wrong ways.',
    metaTitle: 'What Is Adenomyosis? The Science, in Plain Language | Pelvia',
    metaDescription:
      'Adenomyosis is a structural disease of the uterine muscle: endometrial tissue trapped inside the muscle wall. Understand the junctional zone, why it hurts, and what helps.',
    physicianReviewed: true,
    next: 'treatment',
    nextNote: 'The full toolbox for managing the disease and the pain.',
  },
  {
    slug: 'excision-surgery',
    path: '/excision-surgery',
    eyebrow: 'Understanding your options',
    shortTitle: 'Excision surgery',
    title: 'What is excision surgery?',
    oneLiner:
      'The only treatment that removes endometriosis at the root. Everything else manages the symptoms. This removes the disease.',
    metaTitle: 'Excision Surgery for Endometriosis, Explained | Pelvia',
    metaDescription:
      'Excision surgery removes endometriosis lesions at the root instead of burning the surface. Learn how it differs from ablation, why the surgeon matters, and what to expect.',
    physicianReviewed: true,
    next: 'treatment',
    nextNote: 'Where surgery fits among every other way to manage the disease.',
  },
  {
    slug: 'treatment',
    path: '/treatment',
    eyebrow: 'Understanding your options',
    shortTitle: 'Treatment & management',
    title: 'Treatment & management',
    oneLiner:
      'There is no one-size-fits-all treatment for endometriosis or adenomyosis. There is a toolbox, and the right plan depends on your pain, your goals, and your timeline.',
    metaTitle: 'Endometriosis Treatment & Management Options | Pelvia',
    metaDescription:
      'From hormonal management and pelvic floor physical therapy to excision surgery: an honest map of what each treatment can and can’t do, and how to choose your path.',
    physicianReviewed: true,
    // No `next` page — the natural next move is to start an intake.
  },
]

export interface LearnGroup {
  label: string
  slugs: string[]
}

/** Grouping used by the nav dropdown. */
export const LEARN_GROUPS: LearnGroup[] = [
  { label: 'Understand your condition', slugs: ['endometriosis', 'adenomyosis'] },
  { label: 'Understand your options', slugs: ['excision-surgery', 'treatment'] },
]

export function getLearnPage(slug: string): LearnPageMeta | undefined {
  return LEARN_PAGES.find((p) => p.slug === slug)
}

// Maps a user's primary condition/diagnosis to the most relevant Learn slug.
// Returns null for general-wellness / no-diagnosis users — we don't imply a condition they didn't claim.
export function conditionToLearnSlug(condition: string | null | undefined): string | null {
  if (!condition) return null
  if (condition === 'endometriosis') return 'endometriosis'
  if (condition === 'adenomyosis') return 'adenomyosis'
  if (
    condition === 'no_diagnosis' ||
    condition === 'pelvic_tracking' ||
    condition === 'general_wellness'
  ) return null
  // cpp, IC, fibroids, painful_periods, cramps, pfd, something_else, other → broad on-ramp
  return 'treatment'
}
